import * as THREE from 'three'
import { useState } from 'react'
import { Canvas, useLoader } from '@react-three/fiber'
import { MeshTransmissionMaterial, useGLTF, AccumulativeShadows, RandomizedLight, Environment, OrbitControls } from '@react-three/drei'

const MODELS = [
  { id: 'hannah', path: '/hannah.glb', label: 'Hannah' },
  { id: 'couple', path: '/couple.glb', label: 'Josh and Sarah' },
  { id: 'wesley', path: '/wesley.glb', label: 'Wesley' },
]

export default function Cube() {
  const [currentModel, setCurrentModel] = useState('wesley')

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <Canvas shadows camera={{ position: [15, 0, 15], fov: 25 }}>
        <ambientLight intensity={Math.PI} />
        <group position={[0, -2, 0]}>
          <IceCube currentModel={currentModel} />
          <AccumulativeShadows temporal frames={100} alphaTest={0.9} color="#818e96" colorBlend={1} opacity={0.8} scale={20}>
            <RandomizedLight radius={10} ambient={0.5} intensity={Math.PI} position={[2.5, 8, -2.5]} bias={0.001} />
          </AccumulativeShadows>
        </group>
        <OrbitControls minPolarAngle={0} maxPolarAngle={Math.PI / 2} autoRotate autoRotateSpeed={0.05} makeDefault enableZoom={false} />
        <Environment files="https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/dancing_hall_1k.hdr" background backgroundBlurriness={1} />
      </Canvas>
      <ModelSwitcher currentModel={currentModel} onModelChange={setCurrentModel} />
    </div>
  )
}

function ModelSwitcher({ currentModel, onModelChange }) {
  return (
    <div style={{
      position: 'absolute',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: '8px',
      padding: '8px',
      background: 'rgba(255, 255, 255, 0.9)',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    }}>
      {MODELS.map((model) => (
        <button
          key={model.id}
          onClick={() => onModelChange(model.id)}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: currentModel === model.id ? '600' : '400',
            border: 'none',
            borderRadius: '8px',
            background: currentModel === model.id ? '#333' : 'transparent',
            color: currentModel === model.id ? '#fff' : '#333',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            minWidth: '80px',
            touchAction: 'manipulation',
          }}
        >
          {model.label}
        </button>
      ))}
    </div>
  )
}

function IceCube({ currentModel }) {
  const { nodes: iceCubeNodes } = useGLTF('/ice_cube.glb')
  const { scene: hannahScene } = useGLTF('/hannah.glb')
  const { scene: coupleScene } = useGLTF('/couple.glb')
  const { scene: wesleyScene } = useGLTF('/wesley.glb')

  const frostTexture = useLoader(THREE.TextureLoader, '/frost_inverted.png')
  frostTexture.repeat.set(15, 15)
  frostTexture.wrapS = THREE.RepeatWrapping
  frostTexture.wrapT = THREE.RepeatWrapping

  const cubePosition = [0, 1.92, 0]
  const cubeColor = '#bbddff'

  const scenes = { hannah: hannahScene, couple: coupleScene, wesley: wesleyScene }
  const modelScene = scenes[currentModel]

  return (
    <group dispose={null}>
      <mesh
        geometry={iceCubeNodes.cube.geometry}
        position={cubePosition}
        scale={2.5}
        >
        <MeshTransmissionMaterial
          background={new THREE.Color(cubeColor)}
          backside={false}
          samples={10}
          resolution={2048}
          transmission={1}
          thickness={1.5}
          ior={1.31}
          chromaticAberration={0.06}
          anisotropy={0.1}
          distortion={0.0}
          distortionScale={0.3}
          temporalDistortion={0.5}
          clearcoat={0.1}
          attenuationDistance={0.5}
          attenuationColor={'#ffffff'}
          color={cubeColor}
          bg={cubeColor}
          roughnessMap={frostTexture}
          roughness={0.8}
        />
      </mesh>
      <mesh
        castShadow
        renderOrder={-100}
        geometry={iceCubeNodes.cube_inverted.geometry}
        material-side={THREE.FrontSide}
        position={cubePosition}
        scale={2.5}
      />
      <group key={currentModel} scale={1.8} position={[0, 1.9, 0]}>
        <primitive object={modelScene} />
      </group>
    </group>
  )
}

// Preload all models for smooth switching
useGLTF.preload('/hannah.glb')
useGLTF.preload('/couple.glb')
useGLTF.preload('/wesley.glb')
useGLTF.preload('/ice_cube.glb')
